import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { EvidenceUploadField } from './EvidenceUploadField'
import { COMPANY_FORM_FIELDS } from '../constants'
import { requiresEvidence } from '../companies.logic'
import type { Company, CreateCompanyInput, UpdateCompanyInput } from '../types'

const MULTILINE_FIELDS = new Set(['directors', 'address'])

// ─── Zod schema ────────────────────────────────────────────────────────────────

const companyFormSchema = z.object({
  nameTh: z.string().min(1, 'companies.errors.required'),
  nameEn: z.string().min(1, 'companies.errors.required'),
  taxId: z
    .string()
    .min(1, 'companies.errors.required')
    .refine((v) => /^\d{13}$/.test(v.replace(/-/g, '')), {
      message: 'companies.errors.taxIdFormat',
    }),
  registrationNo: z.string().min(1, 'companies.errors.required'),
  registeredCapital: z.string().min(1, 'companies.errors.required'),
  businessType: z.string().min(1, 'companies.errors.required'),
  directors: z.string().min(1, 'companies.errors.required'),
  address: z.string().min(1, 'companies.errors.required'),
  phone: z.string().optional(),
  email: z
    .string()
    .optional()
    .refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), {
      message: 'companies.errors.emailFormat',
    }),
  contactPerson: z.string().optional(),
  contactPhone: z.string().optional(),
})

type CompanyFormValues = z.infer<typeof companyFormSchema>

// ─── Props ──────────────────────────────────────────────────────────────────────

interface CompanyFormProps {
  mode: 'create' | 'edit'
  initialData?: Company
  onSubmit: (values: CreateCompanyInput | UpdateCompanyInput) => Promise<void>
  isLoading: boolean
  onCancel: () => void
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function CompanyForm({
  mode,
  initialData,
  onSubmit,
  isLoading,
  onCancel,
}: CompanyFormProps) {
  const { t } = useTranslation()
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null)
  const [evidenceError, setEvidenceError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: initialData
      ? {
          nameTh: initialData.nameTh,
          nameEn: initialData.nameEn,
          taxId: initialData.taxId,
          registrationNo: initialData.registrationNo,
          registeredCapital: initialData.registeredCapital,
          businessType: initialData.businessType,
          directors: initialData.directors,
          address: initialData.address,
          phone: initialData.phone,
          email: initialData.email,
          contactPerson: initialData.contactPerson,
          contactPhone: initialData.contactPhone,
        }
      : {},
  })

  // Determine if evidence is required (edit: any formal field changed)
  const watchedValues = watch()
  const formalChanges: Record<string, string> = {}
  if (mode === 'edit' && initialData) {
    const initial: Record<keyof CompanyFormValues, string | undefined> = {
      nameTh: initialData.nameTh,
      nameEn: initialData.nameEn,
      taxId: initialData.taxId,
      registrationNo: initialData.registrationNo,
      registeredCapital: initialData.registeredCapital,
      businessType: initialData.businessType,
      directors: initialData.directors,
      address: initialData.address,
      phone: initialData.phone,
      email: initialData.email,
      contactPerson: initialData.contactPerson,
      contactPhone: initialData.contactPhone,
    }
    for (const key of Object.keys(initial) as (keyof CompanyFormValues)[]) {
      const val = watchedValues[key]
      if (initial[key] !== val) {
        formalChanges[key] = val ?? ''
      }
    }
  }
  const needsEvidence = mode === 'edit' && requiresEvidence(formalChanges)

  // ─── Submit handler ──────────────────────────────────────────────────────────

  function handleFormSubmit(values: CompanyFormValues): void {
    if (needsEvidence && !evidenceFile) {
      setEvidenceError(t('companies.errors.evidenceRequired'))
      return
    }
    setEvidenceError(null)

    // Strip undefined values for exactOptionalPropertyTypes compatibility
    const raw = { ...values, evidenceFile: evidenceFile ?? undefined }
    const payload = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined)) as
      | CreateCompanyInput
      | UpdateCompanyInput
    void onSubmit(payload)
  }

  // ─── Style helpers ───────────────────────────────────────────────────────────

  const inputClass = (hasError: boolean) =>
    `h-8 w-full rounded border bg-background px-3 text-xs transition-colors placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 ${
      hasError
        ? 'border-destructive focus:ring-destructive/30'
        : 'border-input hover:border-primary/50'
    }`

  const textareaClass = (hasError: boolean) =>
    `w-full resize-none rounded border bg-background px-3 py-2 text-xs transition-colors placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 ${
      hasError
        ? 'border-destructive focus:ring-destructive/30'
        : 'border-input hover:border-primary/50'
    }`

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(handleFormSubmit)(e)
      }}
      noValidate
    >
      <div className="max-h-[60vh] space-y-5 overflow-y-auto px-1 py-2">
        {/* Registration (formal) fields */}
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h3 className="text-xs font-bold tracking-wider uppercase">
              {t('companies.formalFields')}
            </h3>
            <span className="text-primary text-[10px]">◆</span>
            <p className="text-muted-foreground text-[10px]">{t('companies.formalFieldsHint')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {COMPANY_FORM_FIELDS.registration.map((field) => {
              const fieldKey = field.name as keyof CompanyFormValues
              const hasError = !!errors[fieldKey]
              const isMultiline: boolean = MULTILINE_FIELDS.has(field.name)
              const placeholder = t(
                `companies.placeholders.${field.name}` as Parameters<typeof t>[0],
                {
                  defaultValue: '',
                }
              )
              // Pre-compute values to avoid linter false-positive on as-const union literal types
              const wrapperClass = isMultiline ? 'sm:col-span-2' : ''
              const fieldInput = isMultiline ? (
                <textarea
                  id={`cf-${field.name}`}
                  rows={3}
                  className={textareaClass(hasError)}
                  placeholder={placeholder}
                  {...register(fieldKey)}
                />
              ) : (
                <input
                  id={`cf-${field.name}`}
                  type="text"
                  className={inputClass(hasError)}
                  placeholder={placeholder}
                  {...register(fieldKey)}
                />
              )
              return (
                <div key={field.name} className={wrapperClass}>
                  <div className="mb-1 flex items-center gap-1">
                    <label
                      htmlFor={`cf-${field.name}`}
                      className="text-foreground/70 text-[10px] font-bold tracking-widest uppercase"
                    >
                      {field.isFormal && <span className="text-primary mr-0.5">◆</span>}
                      {t(field.labelKey as Parameters<typeof t>[0])}
                    </label>
                    <span className="text-destructive text-[10px]">*</span>
                  </div>
                  {fieldInput}
                  {errors[fieldKey] && (
                    <p className="text-destructive mt-0.5 text-[10px]">
                      {t(errors[fieldKey].message as Parameters<typeof t>[0], {
                        field: t(field.labelKey as Parameters<typeof t>[0]),
                      })}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className="border-t" />

        {/* Contact fields */}
        <div>
          <div className="mb-3">
            <h3 className="text-xs font-bold tracking-wider uppercase">
              {t('companies.contactInfo')}
            </h3>
            <p className="text-muted-foreground text-[10px]">{t('companies.contactInfoHint')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {COMPANY_FORM_FIELDS.contact.map((field) => {
              const fieldKey = field.name as keyof CompanyFormValues
              const hasError = !!errors[fieldKey]
              const placeholder = t(
                `companies.placeholders.${field.name}` as Parameters<typeof t>[0],
                {
                  defaultValue: '',
                }
              )
              return (
                <div key={field.name}>
                  <label
                    htmlFor={`cf-${field.name}`}
                    className="text-foreground/70 mb-1 block text-[10px] font-bold tracking-widest uppercase"
                  >
                    {t(field.labelKey as Parameters<typeof t>[0])}
                  </label>
                  <input
                    id={`cf-${field.name}`}
                    type={field.name === 'email' ? 'email' : 'text'}
                    className={inputClass(hasError)}
                    placeholder={placeholder}
                    {...register(fieldKey)}
                  />
                  {errors[fieldKey] && (
                    <p className="text-destructive mt-0.5 text-[10px]">
                      {t(errors[fieldKey].message as Parameters<typeof t>[0])}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Evidence / certificate upload */}
        {(mode === 'create' || needsEvidence) && (
          <>
            <div className="border-t" />
            <EvidenceUploadField
              label={
                mode === 'create' ? t('companies.attachCertificate') : t('companies.attachEvidence')
              }
              hint={
                mode === 'create' ? t('companies.certificateHint') : t('companies.evidenceHint')
              }
              value={evidenceFile}
              onChange={(f) => {
                setEvidenceFile(f)
                if (f) setEvidenceError(null)
              }}
              {...(evidenceError ? { error: evidenceError } : {})}
              required={needsEvidence}
            />
          </>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          {t('actions.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={isLoading || (mode === 'edit' && !isDirty)}
          className="min-w-[80px]"
        >
          {isLoading ? (
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {t('actions.save')}
            </span>
          ) : (
            t('actions.save')
          )}
        </Button>
      </div>
    </form>
  )
}
